import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdQueryKey, getEntitiesByFpIdTimelineQueryKey, patchEntitiesByFpIdStatusesMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoCloseSmall16 } from '@onefootprint/icons';
import type { Entity, EntityOrgStatus, EntityStatusEntryOption } from '@onefootprint/request-types/dashboard';
import { Dropdown, useConfirmationDialog } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import PermissionGate from '@/components/permission-gate';
type CustomStatusSelectorsProps = {
  customStatuses: EntityOrgStatus[];
  assignedStatuses: Entity['statuses'];
  entityId: string;
  /** 'row' (default) wraps the selectors horizontally; 'column' stacks them (e.g. inside a popover). */
  direction?: 'row' | 'column';
  /** When true, selecting/removing a status applies immediately without a confirmation dialog. Defaults to false. */
  skipConfirmOnChange?: boolean;
};
const CustomStatusSelectors = t0 => {
  const $ = _c(17);
  const {
    customStatuses,
    assignedStatuses,
    entityId,
    direction: t1,
    skipConfirmOnChange: t2
  } = t0;
  const direction = t1 === undefined ? "row" : t1;
  const skipConfirmOnChange = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "header.customStatusSelectors"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t3);
  const t4 = direction === "column" ? "flex-col items-start" : "items-center flex-wrap";
  let t5;
  if ($[1] !== t4) {
    t5 = cx("flex gap-2", t4);
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== assignedStatuses || $[4] !== customStatuses || $[5] !== entityId || $[6] !== skipConfirmOnChange || $[7] !== t) {
    let t7;
    if ($[9] !== assignedStatuses || $[10] !== entityId || $[11] !== skipConfirmOnChange || $[12] !== t) {
      t7 = customStatus => {
        const assignedEntrySlug = assignedStatuses.find(status => status.kind === customStatus.slug)?.value;
        return <PermissionGate scopeKind="manual_review" fallbackText={t(_temp)} key={customStatus.id}><CustomStatusSelector customStatus={customStatus} assignedEntrySlug={typeof assignedEntrySlug === "string" ? assignedEntrySlug : undefined} entityId={entityId} skipConfirmOnChange={skipConfirmOnChange} /></PermissionGate>;
      };
      $[9] = assignedStatuses;
      $[10] = entityId;
      $[11] = skipConfirmOnChange;
      $[12] = t;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t6 = customStatuses.map(t7);
    $[3] = assignedStatuses;
    $[4] = customStatuses;
    $[5] = entityId;
    $[6] = skipConfirmOnChange;
    $[7] = t;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
type CustomStatusSelectorProps = {
  customStatus: EntityOrgStatus;
  assignedEntrySlug?: string;
  entityId: string;
  disabled?: boolean;
  skipConfirmOnChange?: boolean;
};
type StatusTokenProps = {
  children: string;
  hexCode?: string;
};
const StatusToken = t0 => {
  const $ = _c(5);
  const {
    children,
    hexCode
  } = t0;
  let t1;
  if ($[0] !== hexCode) {
    t1 = {
      borderColor: hexCode,
      color: hexCode
    };
    $[0] = hexCode;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <span className="inline-flex max-w-full items-center rounded border border-tertiary bg-secondary px-1.5 py-0.5 font-mono text-caption-2 text-primary" style={t1}>{children}</span>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const CustomStatusSelector = ({
  customStatus,
  assignedEntrySlug,
  entityId,
  disabled,
  skipConfirmOnChange = false
}: CustomStatusSelectorProps) => {
  const {
    t
  } = useTranslation('entity-details', {
    keyPrefix: 'header.customStatusSelectors'
  });
  const confirmationDialog = useConfirmationDialog();
  const assignedEntityStatusMutation = useMutation(patchEntitiesByFpIdStatusesMutation());
  const showRequestErrorToast = useRequestErrorToast();
  const queryClient = useQueryClient();
  const assignedStatusEntry = customStatus.entries.find(entry => entry.slug === assignedEntrySlug);
  const assignedStatusDisplayName = assignedStatusEntry?.displayName;
  const assignedStatusHexCode = assignedStatusEntry?.hexCode;
  const unassignedStatusDisplayName = t($ => $.unassignedStatus);
  const currentStatusDisplayName = assignedStatusDisplayName || unassignedStatusDisplayName;
  const renderConfirmDescription = (nextDisplayName: string, nextHexCode?: string) => <p className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-2 text-center text-body-2 text-secondary">
      <span>{t($_0 => $_0.confirmChanging)}</span>
      <StatusToken>{customStatus.displayName}</StatusToken>
      <span>{t($_1 => $_1.confirmFrom)}</span>
      <StatusToken hexCode={assignedStatusHexCode}>{currentStatusDisplayName}</StatusToken>
      <span>{t($_2 => $_2.confirmTo)}</span>
      <StatusToken hexCode={nextHexCode}>{nextDisplayName}</StatusToken>
    </p>;
  const assignNewStatus = async (entry_0: EntityStatusEntryOption | null) => {
    try {
      await assignedEntityStatusMutation.mutateAsync({
        path: {
          fpId: entityId
        },
        body: {
          entry: entry_0 ? entry_0.slug : undefined,
          status: customStatus.slug
        }
      });
      queryClient.invalidateQueries({
        queryKey: getEntitiesByFpIdQueryKey({
          path: {
            fpId: entityId
          }
        })
      });
      queryClient.invalidateQueries({
        queryKey: getEntitiesByFpIdTimelineQueryKey({
          path: {
            fpId: entityId
          }
        })
      });
    } catch (error) {
      showRequestErrorToast(error);
    }
  };
  const handleConfirmChangeStatus = (entry_1: EntityStatusEntryOption) => {
    if (skipConfirmOnChange) {
      assignNewStatus(entry_1);
      return;
    }
    confirmationDialog.open({
      title: t($_3 => $_3.applyCustomStatus),
      children: renderConfirmDescription(entry_1.displayName, entry_1.hexCode),
      primaryButton: {
        label: t($_4 => $_4.yes),
        onClick: () => {
          assignNewStatus(entry_1);
        }
      },
      secondaryButton: {
        label: t($_5 => $_5.no)
      }
    });
  };
  const handleRemoveAssignedStatus = () => {
    if (skipConfirmOnChange) {
      assignNewStatus(null);
      return;
    }
    confirmationDialog.open({
      title: t($_6 => $_6.removeCustomStatus),
      children: renderConfirmDescription(unassignedStatusDisplayName),
      primaryButton: {
        label: t($_7 => $_7.yes),
        onClick: () => {
          assignNewStatus(null);
        }
      },
      secondaryButton: {
        label: t($_8 => $_8.no)
      }
    });
  };
  if (!customStatus.entries.length) {
    return null;
  }
  return <div className={cx('flex w-fit rounded-default border border-tertiary max-md:min-h-8', {
    'opacity-50': disabled
  })}>
      <span className="flex items-center justify-center border-r border-tertiary px-2 py-1 text-caption-1 text-primary">
        {customStatus.displayName}
      </span>
      <Dropdown.Root>
        <Dropdown.Trigger disabled={disabled} className={cx('flex items-center justify-center hover:bg-hover px-2 py-1 text-caption-2 text-tertiary', {
        'border-r border-tertiary': assignedStatusDisplayName,
        'cursor-pointer': !disabled,
        'cursor-not-allowed': disabled
      })} style={{
        color: assignedStatusHexCode
      }}>
          {assignedStatusDisplayName || t($_9 => $_9.selectStatus)}
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content>
            <Dropdown.Group>
              {customStatus.entries.map(entry_2 => <Dropdown.Item key={entry_2.slug} style={{
              color: entry_2.hexCode
            }} onSelect={() => handleConfirmChangeStatus(entry_2)}>
                  {entry_2.displayName}
                </Dropdown.Item>)}
            </Dropdown.Group>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      {assignedStatusDisplayName && <button type="button" disabled={disabled} className={cx('flex items-center justify-center hover:bg-hover p-1 max-md:px-2', {
      'cursor-pointer': !disabled,
      'cursor-not-allowed': disabled
    })} onClick={handleRemoveAssignedStatus}>
          <IcoCloseSmall16 />
        </button>}
    </div>;
};
export default CustomStatusSelectors;
function _temp($) {
  return $.notAllowed;
}